// CRUMBLE CAKE (§6.4): 37 flat-top hex tiles out to ring 3, six candles on the ring-2 corners.
//  - TILES (replicated `tileState`, `tileTimer`): SOLID → CRACKING (48) → FALLING (30) → GONE (600)
//    → REGROW (30) → SOLID. A tile cracks the first tick a grounded bean's center is on it (candle
//    tiles only from Sudden Death); jumping preserves tiles. `tileTimer` counts down the ticks left
//    in the current state. Runs in predict mode for the local bean (§18.2).
//  - CANDLES: from the second Ramp bar one candle puffs a flame ring per bar, rotating clockwise
//    from the bottom candle; in Sudden Death every candle fires every 2 bars, as two alternating
//    triangles. Each puff is telegraphed by 1 bar of flicker growth.
//  - SUDDEN DEATH (replicated `sdCursor`): regrowth stops and on every beat the next tile of the
//    crumble order (ring 3 clockwise from the bottom, ring 2's plain tiles, ring 1, the candle
//    tiles) cracks with a 24-tick timer, so it falls on the next beat; each is highlighted one beat
//    before it cracks. The center tile always remains: in Sudden Death it cannot be cracked and,
//    if it was gone, it still regrows, so the last beans standing get a final stage.
import { NO_SLOT, TUNE, VOID } from '../constants';
import { EV, HazardKind, emit } from '../events';
import { BS, Cause, MapId, TileState } from '../ids';
import { isGrounded } from '../sim/edges';
import { isPuppet } from '../sim/world';
import type { Bean, World } from '../types';
import { MAP_TEMPO, beatAtTick, createTempo, roundTempo, tickAtBeat, type Tempo } from './clock';
import { addTelegraph, circleSolid, neutralHazardPose, progress } from './common';
import { createHexGrid, hexIndex, hexRing, hexTileAt, nearestOnHexGrid } from './geometry';
import { dropFromVanishedGround, emitFire, emitWarn, hazardKick } from './hazards';
import { SolidTag, type HazardPose, type MapDef, type Solid } from './mapTypes';
import { datan2 } from '../sim/dmath';

export const TUNING = {
  /** Circumradius R; tile centers x = 1.5·R·q, y = flat·(r + q/2). */
  hexRadius: 1.2702,
  flat: 2.2,
  rings: 3,
  tiles: { crackTicks: 48, fallTicks: 30, goneTicks: 600, regrowTicks: 30, sdCrackTicks: 24 },
  candle: {
    radius: 0.35, top: 2.2,
    /** Flame ring: bodies within `flameRadius` of the candle and below `flameMaxZ` get `flameKb · dizzyMult` outward. */
    flameRadius: 2.0, flameMaxZ: 1.2, flameKb: 9, flameDizzy: 12,
    /** Flicker telegraph length in bars, and how long the ring's visual burst lasts. */
    warnBars: 1, flameVisTicks: 18,
    /** Rotation start (index into `candleTiles`): the bottom candle, nearest the camera. */
    firstCandle: 1,
  },
  /** Ring-2 corner tiles (axial q, r), in clockwise screen order from 30°. */
  candleTiles: [[2, 0], [0, 2], [-2, 2], [-2, 0], [0, -2], [2, -2]],
  /**
   * Spawn tiles: ring 2's six plain tiles and two ring-1 tiles, all ≥ 2.5 m from the rim, ≥ 1.5 m
   * from a candle and 2.2 m apart. §6.4's ring-3 corners sat 1.1 m from the rim (the open
   * cross-package request in DECISIONS.md, resolved in WP2 decisions).
   */
  spawnTiles: [[2, -1], [1, 1], [-1, 2], [-2, 1], [-1, -1], [1, -2], [0, 1], [0, -1]],
  danger: { crackMin: 0.5, flame: 0.9, sdMin: 0.4 },
} as const;

export const CAKE_GRID = createHexGrid(TUNING.hexRadius, TUNING.flat, TUNING.rings);
const G = CAKE_GRID;
const T = TUNING.tiles;
const C = TUNING.candle;
const BEATS_PER_BAR = TUNE.clock.beatsPerBar;

/** Tile indices (into `MapState.tileState`) of the candle tiles, in `TUNING.candleTiles` order. */
export const CANDLE_TILES: readonly number[] = TUNING.candleTiles.map(([q, r]) => hexIndex(G, q, r));
/** The center tile, which never crumbles. */
export const CENTER_TILE = hexIndex(G, 0, 0);
const CANDLE_OF_TILE: readonly number[] = Array.from({ length: G.count }, (_, t) => CANDLE_TILES.indexOf(t));

/** Walkable tile states: bodies stand on SOLID and CRACKING tiles. */
export function tileWalkable(state: number): boolean {
  return state === TileState.SOLID || state === TileState.CRACKING;
}

/** Clockwise (on screen: +y is down) angle from the bottom, in [0, 2π). */
function clockwiseFromBottom(t: number): number {
  const a = datan2(G.cy[t], G.cx[t]) - Math.PI / 2;
  return ((a % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
}

/**
 * The Sudden Death crumble order (§6.4): ring 3 clockwise from the bottom, ring 2's plain tiles,
 * ring 1, then the candle tiles, each clockwise from the bottom. 36 tiles; the center is not in it.
 */
export const CRUMBLE_ORDER: readonly number[] = (() => {
  const group = (t: number): number => {
    const ring = hexRing(G.q[t], G.r[t]);
    if (ring === 3) return 0;
    if (ring === 2) return CANDLE_OF_TILE[t] >= 0 ? 3 : 1;
    return 2;
  };
  const tiles = Array.from({ length: G.count }, (_, t) => t).filter((t) => t !== CENTER_TILE);
  return tiles.sort((a, b) => group(a) - group(b) || clockwiseFromBottom(a) - clockwiseFromBottom(b));
})();
const CRUMBLE_INDEX: readonly number[] = (() => {
  const idx = new Array<number>(G.count).fill(-1);
  CRUMBLE_ORDER.forEach((t, i) => { idx[t] = i; });
  return idx;
})();

const CANDLE_SOLIDS: readonly Solid[] = CANDLE_TILES.map((t, i) => circleSolid(G.cx[t], G.cy[t], C.radius, C.top, TUNE.body.restitution, SolidTag.NONE, i));

const maxX = Math.max(...G.cx) + G.R;
const maxY = Math.max(...G.cy) + G.flat / 2;

const tempo = createTempo();

export function cakeTempo(w: World): Tempo {
  return roundTempo(w.cfg.mapId, w.cfg.modeId, w.cfg.capTickOverride, tempo);
}

/** A candle stands while its tile is walkable (it topples with the tile in Sudden Death). */
export function candleStanding(w: World, i: number): boolean {
  return tileWalkable(w.map.tileState[CANDLE_TILES[i]]);
}

// ---- Candle schedule ----------------------------------------------------------------------------

function barTicks(bar: number, t: Tempo): number {
  return tickAtBeat(t, bar * BEATS_PER_BAR);
}

/** True when candle `i` puffs on the downbeat of bar `bar` (from the second Ramp bar). */
export function candleFiresAtBar(i: number, bar: number, t: Tempo): boolean {
  const rampBar = t.rampBeat / BEATS_PER_BAR;
  const sdBar = t.sdBeat / BEATS_PER_BAR;
  if (!(bar >= rampBar + 1)) return false;
  if (bar < sdBar) return i === (C.firstCandle + (bar - rampBar - 1)) % CANDLE_TILES.length;
  // Sudden Death: alternating triangles, the one through the first candle on even SD bars.
  return (i + C.firstCandle + (bar - sdBar)) % 2 === 0;
}

/** Round tick of candle `i`'s first puff strictly after the bar containing `tick` (or Infinity). */
export function nextCandleFire(i: number, tick: number, t: Tempo): number {
  if (!Number.isFinite(t.rampStart)) return Infinity;
  const start = Math.max(Math.floor(beatAtTick(t, tick) / BEATS_PER_BAR) + 1, t.rampBeat / BEATS_PER_BAR + 1);
  for (let bar = start; bar < start + 2 * CANDLE_TILES.length; bar++) if (candleFiresAtBar(i, bar, t)) return barTicks(bar, t);
  return Infinity;
}

/** Round tick of candle `i`'s latest puff at or before `tick` (or −Infinity). */
export function lastCandleFire(i: number, tick: number, t: Tempo): number {
  if (!Number.isFinite(t.rampStart)) return -Infinity;
  const bar = Math.floor(beatAtTick(t, tick) / BEATS_PER_BAR);
  for (let b = bar; b > bar - 2 * CANDLE_TILES.length && b >= t.rampBeat / BEATS_PER_BAR + 1; b--) {
    if (candleFiresAtBar(i, b, t)) return barTicks(b, t);
  }
  return -Infinity;
}

/** Tick at which the flicker of the puff at `fireTick` starts (1 bar before). */
function candleWarnTick(fireTick: number, t: Tempo): number {
  return tickAtBeat(t, beatAtTick(t, fireTick) - C.warnBars * BEATS_PER_BAR);
}

// ---- Sudden Death crumble -----------------------------------------------------------------------

/** Round tick at which crumble-order entry `k` cracks (beat k of Sudden Death). */
export function crumbleTick(k: number, t: Tempo): number {
  return tickAtBeat(t, t.sdBeat + k);
}

/** Tick at which crumble entry `k` is highlighted (one beat before it cracks). */
export function crumbleWarnTick(k: number, t: Tempo): number {
  return tickAtBeat(t, t.sdBeat + k - 1);
}

// ---- Step ---------------------------------------------------------------------------------------

let fallingTile = -1;

function onFallingTile(x: number, y: number): boolean {
  return hexTileAt(G, x, y) === fallingTile;
}

function setTile(w: World, tile: number, state: number, timer: number, by: number): void {
  w.map.tileState[tile] = state;
  w.map.tileTimer[tile] = timer;
  emit(w, EV.TILE, by, state, tile, 0, G.cx[tile], G.cy[tile]);
  if (state === TileState.FALLING) {
    fallingTile = tile;
    dropFromVanishedGround(w, CAKE, onFallingTile);
  }
}

/** Advances every tile's timer; Sudden Death stops regrowth (GONE stays GONE), except the center's. */
function advanceTiles(w: World, sd: boolean): void {
  const st = w.map.tileState;
  const tm = w.map.tileTimer;
  for (let i = 0; i < G.count; i++) {
    if (st[i] === TileState.SOLID) continue;
    if (st[i] === TileState.GONE && sd && i !== CENTER_TILE) continue;
    if (tm[i] > 0) tm[i]--;
    if (tm[i] > 0) continue;
    switch (st[i]) {
      case TileState.CRACKING: setTile(w, i, TileState.FALLING, T.fallTicks, NO_SLOT); break;
      case TileState.FALLING: setTile(w, i, TileState.GONE, T.goneTicks, NO_SLOT); break;
      case TileState.GONE: setTile(w, i, TileState.REGROW, T.regrowTicks, NO_SLOT); break;
      case TileState.REGROW: setTile(w, i, TileState.SOLID, 0, NO_SLOT); break;
      default: break;
    }
  }
}

/** Beans whose center rests on a tile (grounded, in a state standing on the floor). */
function standsOnFloor(b: Bean): boolean {
  switch (b.state) {
    case BS.FALLING: case BS.GHOST: case BS.RESPAWN: case BS.FROZEN: case BS.GRABBED: case BS.SPUN:
    case BS.FLOAT: case BS.LEDGE: case BS.CLIMB: case BS.YANKED:
      return false;
    default:
      return isGrounded(b) && b.vz <= 0;
  }
}

function crackUnderBeans(w: World, sd: boolean): void {
  for (const b of w.beans) {
    if (!b.active || b.out || isPuppet(w, b.slot) || !standsOnFloor(b)) continue;
    const tile = hexTileAt(G, b.pos.x, b.pos.y);
    if (tile < 0 || w.map.tileState[tile] !== TileState.SOLID) continue;
    if (sd ? tile === CENTER_TILE : CANDLE_OF_TILE[tile] >= 0) continue;
    setTile(w, tile, TileState.CRACKING, T.crackTicks, b.slot);
  }
}

function stepCrumble(w: World, t: Tempo): void {
  const m = w.map;
  const st = m.tileState;
  while (m.sdCursor < CRUMBLE_ORDER.length && w.tick >= crumbleTick(m.sdCursor, t)) {
    const tile = CRUMBLE_ORDER[m.sdCursor];
    m.sdCursor++;
    if (st[tile] === TileState.SOLID) setTile(w, tile, TileState.CRACKING, T.sdCrackTicks, NO_SLOT);
  }
  // A tile that finished regrowing after its turn passed crumbles right away.
  for (let k = 0; k < m.sdCursor; k++) {
    const tile = CRUMBLE_ORDER[k];
    if (st[tile] === TileState.SOLID) setTile(w, tile, TileState.CRACKING, T.sdCrackTicks, NO_SLOT);
  }
}

function crumbleTelegraphs(w: World, t: Tempo): void {
  const k = w.map.sdCursor;
  if (k >= CRUMBLE_ORDER.length || w.tick !== crumbleWarnTick(k, t)) return;
  const tile = CRUMBLE_ORDER[k];
  const fall = crumbleTick(k, t) + T.sdCrackTicks;
  emitWarn(w, HazardKind.TILE_SD, tile, fall - w.tick, G.cx[tile], G.cy[tile]);
}

function stepCandles(w: World, t: Tempo): void {
  if (!Number.isFinite(t.rampStart) || w.tick < t.rampStart) return;
  for (let i = 0; i < CANDLE_TILES.length; i++) {
    if (!candleStanding(w, i)) continue;
    const s = CANDLE_SOLIDS[i];
    const next = nextCandleFire(i, w.tick, t);
    if (candleWarnTick(next, t) === w.tick) emitWarn(w, HazardKind.CANDLE, i, next - w.tick, s.ax, s.ay);
    if (lastCandleFire(i, w.tick, t) !== w.tick) continue;
    emitFire(w, HazardKind.CANDLE, i, s.ax, s.ay);
    for (const b of w.beans) {
      if (!b.active || b.out || b.z >= C.flameMaxZ) continue;
      const dx = b.pos.x - s.ax;
      const dy = b.pos.y - s.ay;
      if (dx * dx + dy * dy > C.flameRadius * C.flameRadius) continue;
      hazardKick(w, b.slot, dx, dy, C.flameKb, { cause: Cause.FLAME, dizzy: C.flameDizzy, scaleByDizzy: true, key: -1, cooldown: 0, carry: false });
    }
  }
}

// ---- Pose and danger ----------------------------------------------------------------------------

function writePose(w: World, tick: number, out: HazardPose): HazardPose {
  neutralHazardPose(out);
  const t = cakeTempo(w);
  if (Number.isFinite(t.rampStart) && tick >= t.rampStart - C.warnBars * BEATS_PER_BAR * t.tpb) {
    for (let i = 0; i < CANDLE_TILES.length; i++) {
      if (!candleStanding(w, i)) continue;
      const next = nextCandleFire(i, tick, t);
      const warn = candleWarnTick(next, t);
      if (tick >= warn) {
        out.candleFlicker[i] = progress(tick, warn, next);
        addTelegraph(out, HazardKind.CANDLE, i, warn, next, CANDLE_SOLIDS[i].ax, CANDLE_SOLIDS[i].ay);
      }
      const last = lastCandleFire(i, tick, t);
      const since = tick - last;
      if (since >= 0 && since < C.flameVisTicks) out.candleFlame[i] = 1 - since / C.flameVisTicks;
    }
  }
  if (tick >= t.sdStart - t.tpb) {
    const k = w.map.sdCursor;
    // Entries already cracking show through tileState; the next one is highlighted.
    for (let j = Math.max(0, k - 1); j <= k && j < CRUMBLE_ORDER.length; j++) {
      const warn = crumbleWarnTick(j, t);
      const crack = crumbleTick(j, t);
      if (tick < warn || tick >= crack) continue;
      const tile = CRUMBLE_ORDER[j];
      out.sdNextTile = tile;
      out.sdNextWarn = progress(tick, warn, crack);
      addTelegraph(out, HazardKind.TILE_SD, tile, warn, crack + T.sdCrackTicks, G.cx[tile], G.cy[tile]);
    }
  }
  return out;
}

function cakeDanger(w: World, x: number, y: number, aheadTicks: number): number {
  const t = cakeTempo(w);
  let d = 0;
  const tile = hexTileAt(G, x, y);
  if (tile >= 0) {
    const state = w.map.tileState[tile];
    if (state === TileState.CRACKING) {
      const left = w.map.tileTimer[tile];
      d = TUNING.danger.crackMin + (1 - TUNING.danger.crackMin) * (1 - Math.min(1, left / T.crackTicks));
    } else if (state === TileState.SOLID && w.tick >= t.sdStart - t.tpb) {
      const k = CRUMBLE_INDEX[tile];
      if (k >= 0) {
        const left = crumbleTick(k, t) + T.sdCrackTicks - w.tick;
        const span = aheadTicks + T.sdCrackTicks;
        if (left <= span) d = TUNING.danger.sdMin + (1 - TUNING.danger.sdMin) * (1 - Math.max(0, left) / span);
      }
    }
  }
  if (Number.isFinite(t.rampStart) && w.tick + aheadTicks >= t.rampStart) {
    const reach = (C.flameRadius + TUNE.bean.radius) ** 2;
    for (let i = 0; i < CANDLE_TILES.length; i++) {
      if (!candleStanding(w, i)) continue;
      const s = CANDLE_SOLIDS[i];
      if ((x - s.ax) ** 2 + (y - s.ay) ** 2 > reach) continue;
      if (nextCandleFire(i, w.tick, t) - w.tick <= aheadTicks) d = Math.max(d, TUNING.danger.flame);
    }
  }
  return d;
}

export const CAKE: MapDef = {
  id: MapId.CAKE,
  name: 'Crumble Cake',
  tpb: MAP_TEMPO[MapId.CAKE].tpb,
  tpbSD: MAP_TEMPO[MapId.CAKE].tpbSD,
  bounds: { minX: -maxX, maxX, minY: -maxY, maxY },
  spawns: TUNING.spawnTiles.map(([q, r]) => {
    const t = hexIndex(G, q, r);
    return { x: G.cx[t], y: G.cy[t] };
  }),
  voidKind: 'milk',
  center: { x: 0, y: 0 },
  initState(w) {
    w.map.tileState.fill(TileState.SOLID);
    w.map.tileTimer.fill(0);
    w.map.sdCursor = 0;
  },
  groundAt(w, x, y) {
    const t = hexTileAt(G, x, y);
    return t >= 0 && tileWalkable(w.map.tileState[t]) ? 0 : VOID;
  },
  nearestGround(w, x, y, out) {
    return nearestOnHexGrid(G, w.map.tileState, tileWalkable, x, y, out);
  },
  solids(w, out) {
    let n = 0;
    for (let i = 0; i < CANDLE_SOLIDS.length; i++) if (candleStanding(w, i)) out[n++] = CANDLE_SOLIDS[i];
    return n;
  },
  onSolidContact() {},
  step(w) {
    if (w.tick < 0) return;
    const t = cakeTempo(w);
    const sd = w.tick >= t.sdStart;
    advanceTiles(w, sd);
    if (sd) stepCrumble(w, t);
    crumbleTelegraphs(w, t);
    crackUnderBeans(w, sd);
    stepCandles(w, t);
  },
  danger(w, x, y, aheadTicks) {
    return cakeDanger(w, x, y, aheadTicks);
  },
  hazardPose(w, tick, out) {
    return writePose(w, tick, out);
  },
  crateExclusion(w, x, y) {
    const pad = TUNE.crate.radius;
    const t = cakeTempo(w);
    const flames = Number.isFinite(t.rampStart) && w.tick >= t.rampStart - BEATS_PER_BAR * t.tpb;
    for (let i = 0; i < CANDLE_SOLIDS.length; i++) {
      if (!candleStanding(w, i)) continue;
      const s = CANDLE_SOLIDS[i];
      const r = flames ? C.flameRadius : s.r + pad;
      if ((x - s.ax) ** 2 + (y - s.ay) ** 2 <= r * r) return true;
    }
    const tile = hexTileAt(G, x, y);
    if (tile < 0 || w.map.tileState[tile] !== TileState.SOLID) return true;
    if (w.tick >= t.sdStart - BEATS_PER_BAR * t.tpb) {
      const k = CRUMBLE_INDEX[tile];
      if (k >= 0 && crumbleTick(k, t) - w.tick < TUNE.crate.lifeTicks / 2) return true;
    }
    return false;
  },
};
